import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApplicationInsightsService } from 'app/shared/services/application-insights.service';
/* import { NotificationService } from 'app/shared/services/notification.service'; */
import { IOffering } from 'app/models';
import { IBaseApiResponse } from 'app/shared/services/base/base-api.service';
import { NotificationService, ToastNotificationType } from 'app/shared/services/notification.service';
import { LoadingService } from 'app/shared/utilities/loading.service';
import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { AdministrationActions } from '.';
import { IPartnerAccountingTotals, IPartnerLedgerItem, IPartnerThemeView, IPayoutDetail, ITotals } from '../models';
import { IPartnerBase } from '../models/partner-base';
import { ISendMotivationalNudgeResult } from '../models';
import { AdministrationApiService } from '../services/remote/administration-api.service';
import { AdminUserSearchActions, MotivationalNudgeActions, OfferingContactsActions, PartnerUsersActions } from './actions';

@Injectable()
export class AdministrationEffects {
    debounceTimeInMilliseconds: number = 200;

    loadVerificationOfCoverages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadPaymentQueue),
            exhaustMap(() => {
                this.loadingService.addLoading(payoutsLoadingString);
                return this.apiService.getPaymentQueue().pipe(
                    map((details: IPayoutDetail[]) => {
                        this.loadingService.removeLoading(payoutsLoadingString);
                        return AdministrationActions.getPaymentQueue({ details });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(payoutsLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'getVerificationOfCoverages', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    loadTotals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadTotals),
            exhaustMap(() => {
                this.loadingService.addLoading(totalsLoadingString);
                return this.apiService.getTotals().pipe(
                    map((totals: ITotals) => {
                        this.loadingService.removeLoading(totalsLoadingString);
                        return AdministrationActions.getTotals({ totals });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(totalsLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'getTotals', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    setPayoutStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.setPayoutStatus),
            exhaustMap((action) => {
                this.loadingService.addLoading(approvePayoutLoadingString);
                return this.apiService.setIntroductionMeeting(action.status).pipe(
                    switchMap((response: IBaseApiResponse<{}>) => {
                        this.loadingService.removeLoading(approvePayoutLoadingString);
                        if (!response.success) {
                            this.notificationService.showNotification(ToastNotificationType.Error, 'An error approving payout.');
                            return [AdministrationActions.apiErrorAction({ method: 'setIntroductionMeeting', error: `${response}` })];
                        } else {
                            this.notificationService.showNotification(ToastNotificationType.Success, `Payout approved!`);
                            return [AdministrationActions.setPayoutStatusSuccess(), AdministrationActions.loadPaymentQueue()];
                        }
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(approvePayoutLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'setIntroductionMeeting', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    getPayoutDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadPayoutDocument),
            exhaustMap((action) => {
                return this.apiService.getPayoutDocument(action.payoutUuid, action.storageName).pipe(
                    map((documentUrl: string) => {
                        return AdministrationActions.getPayoutDocument({ documentUrl });
                    }),
                    catchError((error: Error) => {
                        return of(AdministrationActions.apiErrorAction({ method: 'getPayoutDocument', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    loadPartners$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadPartners),
            exhaustMap((action) => {
                return this.apiService.getPatners(action.includeBeacon).pipe(
                    map((partners: IPartnerBase[]) => {
                        return AdministrationActions.getPartners({ partners });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(totalsLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'getTotals', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    setPartnerDeposit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.setPartnerDeposit),
            exhaustMap((action) => {
                this.loadingService.addLoading(depositLoadingString);
                return this.apiService.partnerDeposit(action.deposit).pipe(
                    switchMap((response: IBaseApiResponse<{}>) => {
                        this.loadingService.removeLoading(depositLoadingString);
                        if (!response.success) {
                            this.notificationService.showNotification(ToastNotificationType.Error, 'An error occurred with deposit.');
                            return [AdministrationActions.apiErrorAction({ method: 'partnerDeposit', error: `${response}` })];
                        } else {
                            this.notificationService.showNotification(ToastNotificationType.Success, `Deposit succesful!`);
                            return [
                                AdministrationActions.setPartnerDepositSuccess(),
                                AdministrationActions.loadPartnerAccoutingTotals({ partnerUuid: action.deposit.partnerUuid }),
                                AdministrationActions.loadPartnerAccoutingItems({ partnerUuid: action.deposit.partnerUuid }),
                            ];
                        }
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(depositLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'setIntroductionMeeting', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    loadPartnerAccoutingTotals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadPartnerAccoutingTotals),
            exhaustMap((action) => {
                return this.apiService.getPartnerAccountingTotals(action.partnerUuid).pipe(
                    map((totals: IPartnerAccountingTotals) => {
                        return AdministrationActions.getPartnerAccoutingTotals({ totals });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(totalsLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'getPartnerAccountingTotals', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    loadPartnerAccoutingItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadPartnerAccoutingItems),
            exhaustMap((action) => {
                this.loadingService.addLoading(ledgerLoadingString);
                return this.apiService.getPartnerAccountingItems(action.partnerUuid).pipe(
                    map((items: IPartnerLedgerItem[]) => {
                        this.loadingService.removeLoading(ledgerLoadingString);
                        return AdministrationActions.getPartnerAccoutingItems({ items });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(ledgerLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'getPartnerAccountingItems', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    loadTheme$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadTheme),
            exhaustMap((action) => {
                this.loadingService.addLoading(partnerThemeLoadingString);
                return this.apiService.getTheme(action.partnerUuid).pipe(
                    map((theme: IPartnerThemeView) => {
                        this.loadingService.removeLoading(partnerThemeLoadingString);
                        return AdministrationActions.getTheme({ theme });
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(partnerThemeLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'getTheme', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    saveTheme$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.saveTheme),
            exhaustMap((action) => {
                this.loadingService.addLoading(partnerThemeSaveLoadingString);
                return this.apiService.saveTheme(action.partnerUuid, action.payload).pipe(
                    switchMap((response: IBaseApiResponse<IPartnerThemeView>) => {
                        this.loadingService.removeLoading(partnerThemeSaveLoadingString);
                        if (!response.success) {
                            this.notificationService.showNotification(ToastNotificationType.Error, 'An error occurred with saving theme.');
                            return [AdministrationActions.apiErrorAction({ method: 'saveTheme', error: `${response}` })];
                        } else {
                            /* this.notificationService.showNotification(ToastNotificationType.Success, `Deposit succesful!`); */
                            return [AdministrationActions.saveThemeSuccess({ theme: response.body })];
                        }
                    }),
                    catchError((error: Error) => {
                        this.loadingService.removeLoading(partnerThemeSaveLoadingString);
                        return of(AdministrationActions.apiErrorAction({ method: 'saveTheme', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    removeAsset$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.removeAsset),
            exhaustMap((action) => {
                return this.apiService.removeAsset(action.partnerUuid, action.assetType).pipe(
                    switchMap((response: boolean) => {
                        if (!response) {
                            this.notificationService.showNotification(ToastNotificationType.Error, 'An error occurred with removing asset.');
                            return [AdministrationActions.apiErrorAction({ method: 'removeAsset', error: `${response}` })];
                        } else {
                            return [AdministrationActions.removeAssetSuccess()];
                        }
                    }),
                    catchError((error: Error) => {
                        return of(AdministrationActions.apiErrorAction({ method: 'removeAsset', error: JSON.stringify(error) }));
                    })
                );
            })
        )
    );

    loadOfferings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadOfferings),
            switchMap(({ companyId }) =>
                this.apiService.getOfferings(companyId).pipe(
                    map((offerings) => AdministrationActions.loadOfferingsSuccess({ offerings })),
                    catchError((error) =>
                        of(
                            AdministrationActions.loadOfferingsFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    loadOffering$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.loadOffering),
            switchMap(({ offeringId }) =>
                this.apiService.getOffering(offeringId).pipe(
                    map((offering) => AdministrationActions.loadOfferingSuccess({ offering })),
                    catchError((error) =>
                        of(
                            AdministrationActions.loadOfferingFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    createOffering$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.createOffering),
            switchMap(({ offering }) =>
                this.apiService.createOffering(this.toApiModel(offering)).pipe(
                    map((response: IBaseApiResponse<IOffering>) => {
                        if (response.success) {
                            return AdministrationActions.createOfferingSuccess({
                                offering: response.body,
                            });
                        }
                        return AdministrationActions.apiErrorAction({ method: 'createOffering', error: `${response}` });
                    }),
                    catchError((error) =>
                        of(
                            AdministrationActions.createOfferingFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    saveOffering$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.saveOffering),
            switchMap(({ offering }) =>
                this.apiService.updateOffering(this.toApiModel(offering)).pipe(
                    map((response: IBaseApiResponse<IOffering>) => {
                        if (response.success) {
                            return AdministrationActions.saveOfferingSuccess({
                                offering: response.body,
                            });
                        }
                        return AdministrationActions.apiErrorAction({ method: 'updateOffering', error: `${response}` });
                    }),

                    catchError((error) =>
                        of(
                            AdministrationActions.saveOfferingFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    deleteOffering$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdministrationActions.deleteOffering),
            switchMap(({ offeringId }) =>
                this.apiService.deleteOffering(offeringId).pipe(
                    map(() => AdministrationActions.deleteOfferingSuccess({ offeringId })),
                    catchError((error) =>
                        of(
                            AdministrationActions.deleteOfferingFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    loadCompanyUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PartnerUsersActions.loadCompanyUsers),
            switchMap(({ companyId }) =>
                this.apiService.getCompanyUsers(companyId).pipe(
                    map((users) => PartnerUsersActions.loadCompanyUsersSuccess({ companyId, users })),
                    catchError((error) => of(PartnerUsersActions.loadCompanyUsersFailure({ error: this.getErrorMessage(error) })))
                )
            )
        )
    );

    addCompanyUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PartnerUsersActions.addCompanyUser),
            switchMap(({ companyId, userId }) =>
                this.apiService.addPartnerUser(companyId, userId).pipe(
                    map(() => PartnerUsersActions.addCompanyUserSuccess({ companyId })),
                    catchError((error) => of(PartnerUsersActions.addCompanyUserFailure({ error: this.getErrorMessage(error) })))
                )
            )
        )
    );

    reloadAfterPartnerUserMutation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PartnerUsersActions.addCompanyUserSuccess, PartnerUsersActions.removeCompanyUserSuccess),
            map(({ companyId }) => PartnerUsersActions.loadCompanyUsers({ companyId }))
        )
    );

    removeCompanyUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PartnerUsersActions.removeCompanyUser),
            switchMap(({ companyId, userId }) =>
                this.apiService.deleteParnterUser(companyId, userId).pipe(
                    map(() => PartnerUsersActions.removeCompanyUserSuccess({ companyId })),
                    catchError((error) => of(PartnerUsersActions.removeCompanyUserFailure({ error: this.getErrorMessage(error) })))
                )
            )
        )
    );

    searchUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdminUserSearchActions.searchUsers),
            /* map(({ query }) => query.trim()),
            filter((query) => query.length >= 2), */
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((action) =>
                this.apiService.searchPlatformUsers(action.query, action.partnerId).pipe(
                    map((results) => AdminUserSearchActions.searchUsersSuccess({ query: action.query, results })),
                    catchError((error) =>
                        of(
                            AdminUserSearchActions.searchUsersFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    sendNudge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MotivationalNudgeActions.sendNudge),
            exhaustMap((action) =>
                this.apiService.sendMotivationalNudge(action.request).pipe(
                    map((response: IBaseApiResponse<ISendMotivationalNudgeResult>) => {
                        if (!response.success || !response.body) {
                            this.notificationService.showNotification(ToastNotificationType.Error, 'An error sending the nudge.');
                            return MotivationalNudgeActions.sendNudgeFailure({ error: `${response}` });
                        }

                        const { recipientCount, failedAttemptCount, undeliverableCount } = response.body;
                        if (failedAttemptCount === 0 && undeliverableCount === 0) {
                            this.notificationService.showNotification(ToastNotificationType.Success, `Nudge sent to ${recipientCount} recipient(s)!`);
                        } else {
                            // Genuine failures and capability gaps are distinct causes (BL-305) — a
                            // mixed outcome must call out both, not let one silently eclipse the other.
                            const reasons: string[] = [];
                            if (failedAttemptCount > 0) {
                                reasons.push(`${failedAttemptCount} delivery attempt(s) failed — check the logs before retrying`);
                            }
                            if (undeliverableCount > 0) {
                                reasons.push(`${undeliverableCount} could not be reached (no push device or email on file for the requested channel)`);
                            }
                            this.notificationService.showNotification(ToastNotificationType.Warn, `Nudge sent to ${recipientCount} recipient(s). ${reasons.join('; ')}.`);
                        }
                        return MotivationalNudgeActions.sendNudgeSuccess({ result: response.body });
                    }),
                    catchError((error: Error) => {
                        this.notificationService.showNotification(ToastNotificationType.Error, 'An error sending the nudge.');
                        return of(MotivationalNudgeActions.sendNudgeFailure({ error: JSON.stringify(error) }));
                    })
                )
            )
        )
    );

    loadOfferingContacts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OfferingContactsActions.loadOfferingContacts),
            switchMap(({ offeringId }) =>
                this.apiService.getOfferingContacts(offeringId).pipe(
                    map((contacts) =>
                        OfferingContactsActions.loadOfferingContactsSuccess({
                            offeringId,
                            contacts,
                        })
                    ),
                    catchError((error) =>
                        of(
                            OfferingContactsActions.loadOfferingContactsFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    addOfferingContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OfferingContactsActions.addOfferingContact),
            switchMap(({ offeringId, request }) =>
                this.apiService.addOfferingContact(offeringId, request).pipe(
                    map(() => OfferingContactsActions.addOfferingContactSuccess({ offeringId })),
                    catchError((error) =>
                        of(
                            OfferingContactsActions.addOfferingContactFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    updateOfferingContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OfferingContactsActions.updateOfferingContact),
            switchMap(({ offeringId, userId, request }) =>
                this.apiService.updateOfferingContact(offeringId, userId, request).pipe(
                    map(() => OfferingContactsActions.updateOfferingContactSuccess({ offeringId })),
                    catchError((error) =>
                        of(
                            OfferingContactsActions.updateOfferingContactFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    deleteOfferingContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OfferingContactsActions.deleteOfferingContact),
            switchMap(({ offeringId, userId }) =>
                this.apiService.deleteOfferingContact(offeringId, userId).pipe(
                    map(() => OfferingContactsActions.deleteOfferingContactSuccess({ offeringId })),
                    catchError((error) =>
                        of(
                            OfferingContactsActions.deleteOfferingContactFailure({
                                error: this.getErrorMessage(error),
                            })
                        )
                    )
                )
            )
        )
    );

    reloadAfterMutation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OfferingContactsActions.addOfferingContactSuccess, OfferingContactsActions.updateOfferingContactSuccess, OfferingContactsActions.deleteOfferingContactSuccess),
            map(({ offeringId }) => OfferingContactsActions.loadOfferingContacts({ offeringId }))
        )
    );

    private toApiModel(offering: IOffering): IOffering {
        return {
            ...offering,
            id: offering.id > 0 ? offering.id : 0,
            details: offering.details.map((detail) => ({
                ...detail,
                id: detail.id > 0 ? detail.id : 0,
                offeringId: detail.offeringId && detail.offeringId > 0 ? detail.offeringId : 0,
                items: detail.items.map((item) => ({
                    ...item,
                    id: item.id > 0 ? item.id : 0,
                    /* detailId: item.detailId && item.detailId > 0 ? item.detailId : 0, */
                })),
            })),
        };
    }

    private getErrorMessage(error: unknown): string {
        if (typeof error === 'string') {
            return error;
        }

        if (error && typeof error === 'object') {
            const maybeError = error as {
                error?: { message?: string };
                message?: string;
            };

            if (maybeError.error?.message) {
                return maybeError.error.message;
            }

            if (maybeError.message) {
                return maybeError.message;
            }
        }

        return 'An unexpected error occurred.';
    }

    apiErrorAction$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AdministrationActions.apiErrorAction),
                tap((action) => {
                    this.applicationInsights.trackException(JSON.parse(JSON.stringify(action.error)), [{ key: 'methodName', value: action.method }]);
                })
            ),
        { dispatch: false }
    );
    constructor(
        private readonly actions$: Actions,
        private apiService: AdministrationApiService,
        private readonly applicationInsights: ApplicationInsightsService,
        private readonly loadingService: LoadingService,
        private readonly notificationService: NotificationService
    ) {}
}

export const payoutsLoadingString: string = 'load-admin-payouts';
export const totalsLoadingString: string = 'load-admin-totals';
export const approvePayoutLoadingString: string = 'approve-payout';
export const depositLoadingString: string = 'partner-deposit';
export const ledgerLoadingString: string = 'partner-ledger-items';

export const partnerThemeLoadingString: string = 'load-partner-theme';
export const partnerThemeSaveLoadingString: string = 'save-partner-theme';
